import { convertWeight } from '../unit-services/weights/weight-convert-service';

const getEmptyWeightInSellerWeightUnit = (sellerUnitOfWeight) => {
  return {
    unit: sellerUnitOfWeight,
    value: 0,
  };
};

const getTotalWeightOfReturnItems = (returnItemWeights, sellerDefaultWeightUnit) => {
  const totalWeightValueOfAllItems = returnItemWeights.reduce((totalWeightValue, returnItemWeight) => {
    return totalWeightValue + (convertWeight(returnItemWeight, sellerDefaultWeightUnit).value || 0);
  }, 0);

  return {
    unit: sellerDefaultWeightUnit,
    value: totalWeightValueOfAllItems,
  };
};

const calculateWeightFromFulfillment = (fulfillment, fulfillmentPlans, selectedReturnItems, sellerDefaultWeightUnit) => {
  if (!fulfillment?.weight || !fulfillment.weight.unit || !fulfillment.weight.value) {
    return getEmptyWeightInSellerWeightUnit(sellerDefaultWeightUnit);
  }

  const fulfillmentPlan = fulfillmentPlans.find((fp) => fp.fulfillmentPlanId === fulfillment.fulfillmentPlanId);
  if (!fulfillmentPlan || !fulfillmentPlan.items.length) {
    return getEmptyWeightInSellerWeightUnit(sellerDefaultWeightUnit);
  }

  const totalQuantityOfItemsInFulfillmentPlan = fulfillmentPlan.items.reduce((total, { quantity }) => total + quantity, 0);
  if (!totalQuantityOfItemsInFulfillmentPlan) {
    return getEmptyWeightInSellerWeightUnit(sellerDefaultWeightUnit);
  }

  const totalQuantityOfItemsInReturn = selectedReturnItems.reduce((total, { quantity }) => total + quantity, 0);
  if (!totalQuantityOfItemsInReturn) {
    return getEmptyWeightInSellerWeightUnit(sellerDefaultWeightUnit);
  }

  if (totalQuantityOfItemsInFulfillmentPlan > totalQuantityOfItemsInReturn) {
    return getEmptyWeightInSellerWeightUnit(sellerDefaultWeightUnit);
  }

  return convertWeight(
    {
      unit: fulfillment.weight.unit,
      value: (fulfillment.weight.value / totalQuantityOfItemsInFulfillmentPlan) * totalQuantityOfItemsInReturn,
    },
    sellerDefaultWeightUnit,
  );
};

export const calculateReturnShipmentWeight = ({
  sellerDefaultWeightUnit,
  selectedReturnItems = [],
  fulfillmentPlans = [],
  fulfillments = [],
}) => {
  const fulfillmentPlanItems = fulfillmentPlans.reduce((items, fulfillmentPlan) => {
    items.push(...(fulfillmentPlan.items || []));
    return items;
  }, []);

  const returnItemWeights = selectedReturnItems.map(({ fulfillmentPlanItemId, quantity }) => {
    const fulfillmentPlanItem = fulfillmentPlanItems.find((item) => item.fulfillmentPlanItemId === fulfillmentPlanItemId) || {
      weight: { unit: sellerDefaultWeightUnit, value: 0 },
    };
    const fulfillmentPlanItemWeight = fulfillmentPlanItem.weight || {
      unit: sellerDefaultWeightUnit,
      value: 0,
    };
    return {
      unit: fulfillmentPlanItemWeight.unit,
      value: fulfillmentPlanItemWeight.value * quantity,
    };
  });

  const nonVoidedFulfillments = fulfillments.filter((fill) => !fill.isLabelVoided);
  const doAllReturnItemsHaveWeights = returnItemWeights.every(({ value }) => !!value);
  const noFulfillmentsAndCannotCalculateWeight = !fulfillments.length && !doAllReturnItemsHaveWeights;

  if (noFulfillmentsAndCannotCalculateWeight) {
    return getEmptyWeightInSellerWeightUnit(sellerDefaultWeightUnit);
  }

  if (doAllReturnItemsHaveWeights) {
    return getTotalWeightOfReturnItems(returnItemWeights, sellerDefaultWeightUnit);
  }

  if (nonVoidedFulfillments.length === 1 || !doAllReturnItemsHaveWeights) {
    return calculateWeightFromFulfillment(nonVoidedFulfillments[0], fulfillmentPlans, selectedReturnItems, sellerDefaultWeightUnit);
  }

  return getEmptyWeightInSellerWeightUnit(sellerDefaultWeightUnit);
};
